import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { Checkbox, Dropdown, Form } from "antd";
import clsx from "clsx";
import { Controller, Control, UseFormSetValue, useFormState } from "react-hook-form";
import type { GetTitleJournalResponse } from "@/entities/api/journal/journal-title-types";
import { CloseIcon } from "@/shared/assets/svg/icons";
import type { CompanyItem } from "@/shared/ui";
import { useLocalStorage } from "@shared/lib/hooks";
import { parseJwtCompanies } from "../../../../model/utils/parse-jwt-companies";
import {
  getCompanyDisplayName,
  getTitleJournalCompanies,
  resolveUserTitleCompanyMatch,
  type TitleJournalCompanyOption,
} from "../../../../model/utils/title-journal-companies";
import { SectionThirdFormType } from "../../model/types";
import "./section-company-multi-select.scss";

const CHIP_GAP = 8;
const OVERFLOW_BADGE_WIDTH = 36;
const MIN_CHIP_WIDTH = 100;
const MAX_VISIBLE_CHIPS = 3;

interface SelectedCompaniesChipsProps {
  selectedCompanies: TitleJournalCompanyOption[];
  onRemove: (item: TitleJournalCompanyOption) => void;
  onOpenSelect: () => void;
  isSelectDisabled: boolean;
}

function SelectedCompaniesChips({
  selectedCompanies,
  onRemove,
  onOpenSelect,
  isSelectDisabled,
}: SelectedCompaniesChipsProps) {
  const chipsRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  useLayoutEffect(() => {
    const chipsEl = chipsRef.current;
    if (!chipsEl) return undefined;

    const calculate = () => {
      if (selectedCompanies.length === 0) {
        setVisibleCount(0);
        return;
      }

      const availableWidth = chipsEl.clientWidth;
      if (availableWidth <= 0) return;

      let count = 0;
      const maxCandidate = Math.min(
        selectedCompanies.length,
        MAX_VISIBLE_CHIPS,
      );

      for (let candidate = maxCandidate; candidate >= 1; candidate -= 1) {
        const hidden = selectedCompanies.length - candidate;
        const overflowReserve =
          hidden > 0 ? OVERFLOW_BADGE_WIDTH + CHIP_GAP : 0;
        const gapsWidth = Math.max(candidate - 1, 0) * CHIP_GAP;
        const availableForChips =
          availableWidth - overflowReserve - gapsWidth;
        const chipWidth = availableForChips / candidate;

        if (chipWidth >= MIN_CHIP_WIDTH) {
          count = candidate;
          break;
        }
      }

      setVisibleCount(count);
    };

    calculate();

    const observer = new ResizeObserver(calculate);
    observer.observe(chipsEl);

    return () => observer.disconnect();
  }, [selectedCompanies]);

  const resolvedVisibleCount =
    visibleCount ?? Math.min(selectedCompanies.length, MAX_VISIBLE_CHIPS);
  const visibleCompanies = selectedCompanies.slice(0, resolvedVisibleCount);
  const hiddenCount = selectedCompanies.length - visibleCompanies.length;

  const renderChip = (company: TitleJournalCompanyOption) => (
    <span
      key={company.projectParticipantId}
      className="company-multi-select__chip"
    >
      <span className="company-multi-select__chip-label">
        {getCompanyDisplayName(company)}
      </span>
      {!isSelectDisabled && (
        <button
          type="button"
          className="company-multi-select__chip-remove"
          aria-label="Удалить"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(company);
          }}
        >
          <CloseIcon />
        </button>
      )}
    </span>
  );

  if (selectedCompanies.length === 0) {
    return (
      <span className="company-multi-select__placeholder">
        Организация не выбрана
      </span>
    );
  }

  return (
    <div ref={chipsRef} className="company-multi-select__chips">
      {visibleCompanies.map((company) => renderChip(company))}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="company-multi-select__overflow"
          onClick={(e) => {
            e.stopPropagation();
            onOpenSelect();
          }}
        >
          +{hiddenCount}
        </button>
      )}
    </div>
  );
}

interface SectionCompanyMultiSelectProps {
  control: Control<SectionThirdFormType>;
  setValue: UseFormSetValue<SectionThirdFormType>;
  titleJournal?: GetTitleJournalResponse;
  isCreateMode?: boolean;
  initialCompanyList?: CompanyItem[] | null;
  disabled?: boolean;
}

function toOptionFromCompanyItem(company: CompanyItem): TitleJournalCompanyOption | null {
  if (!company.companyId || !company.projectParticipantId) return null;

  return {
    companyId: company.companyId,
    projectParticipantId: company.projectParticipantId,
    shortName: company.shortName ?? "",
    fullName: company.fullName ?? "",
    role: "otherDeveloper",
  };
}

export function SectionCompanyMultiSelect({
  control,
  setValue,
  titleJournal,
  isCreateMode = false,
  initialCompanyList,
  disabled = false,
}: SectionCompanyMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const hasPrefilled = useRef(false);
  const [newToken] = useLocalStorage("newToken");

  const userCompanyIds = useMemo(
    () => parseJwtCompanies(newToken),
    [newToken],
  );

  const titleCompanies = useMemo(
    () => getTitleJournalCompanies(titleJournal),
    [titleJournal],
  );

  const userMatch = useMemo(
    () => resolveUserTitleCompanyMatch(titleJournal, userCompanyIds),
    [titleJournal, userCompanyIds],
  );

  const companyOptions = useMemo<TitleJournalCompanyOption[]>(() => {
    const options = new Map<string, TitleJournalCompanyOption>();

    for (const company of titleCompanies) {
      options.set(company.projectParticipantId, company);
    }

    for (const company of initialCompanyList ?? []) {
      const option = toOptionFromCompanyItem(company);
      if (!option || options.has(option.projectParticipantId)) {
        // eslint-disable-next-line no-continue
        continue;
      }
      // eslint-disable-next-line no-continue
      options.set(option.projectParticipantId, option);
    }

    return Array.from(options.values());
  }, [titleCompanies, initialCompanyList]);

  const selectableOptions = useMemo(() => {
    if (userMatch?.isLos) {
      return companyOptions;
    }

    if (userMatch) {
      return companyOptions.filter(
        (company) => company.projectParticipantId === userMatch.projectParticipantId,
      );
    }

    return companyOptions;
  }, [companyOptions, userMatch]);

  // Не-ЛОС с матчем в титуле — только своя орг, селект залочен.
  // Иначе (ЛОС или нет матча) — мультивыбор, без single-select по списку.
  const isSelectDisabled = disabled || Boolean(userMatch && !userMatch.isLos);
  const isMultiEnabled = !isSelectDisabled;

  useEffect(() => {
    if (!isCreateMode || hasPrefilled.current || !userMatch) return;

    const { companyId, projectParticipantId } = userMatch;
    if (companyId && projectParticipantId) {
      setValue("companyList", [{ companyId, projectParticipantId }], {
        shouldValidate: false,
      });
      hasPrefilled.current = true;
    }
  }, [isCreateMode, userMatch, setValue]);

  const { errors } = useFormState({ control, name: ["companyList"] });
  const companyListError =
    (errors.companyList as { message?: string; root?: { message?: string } })
      ?.message ??
    (errors.companyList as { root?: { message?: string } })?.root?.message;

  return (
    <Form.Item
      label="Организация, выполнившая работу"
      validateStatus={companyListError ? "error" : ""}
      help={companyListError?.toString()}
      style={{ marginBottom: 24 }}
    >
      <Controller
        name="companyList"
        control={control}
        render={({ field }) => {
          const selectedIds =
            field.value?.map((c) => c.projectParticipantId) ?? [];

          const selectedCompanies = companyOptions.filter((p) =>
            selectedIds.includes(p.projectParticipantId),
          );

          const handleToggle = (
            item: TitleJournalCompanyOption,
            checked: boolean,
          ) => {
            if (isSelectDisabled) return;

            if (isMultiEnabled) {
              const next = checked
                ? [
                    ...(field.value ?? []),
                    {
                      companyId: item.companyId,
                      projectParticipantId: item.projectParticipantId,
                    },
                  ]
                : (field.value ?? []).filter(
                    (c) => c.projectParticipantId !== item.projectParticipantId,
                  );
              field.onChange(next);
              return;
            }

            field.onChange(
              checked
                ? [
                    {
                      companyId: item.companyId,
                      projectParticipantId: item.projectParticipantId,
                    },
                  ]
                : [],
            );
          };

          const dropdownMenu = (
            <div
              className="company-multi-select__menu"
              onMouseDown={(e) => e.preventDefault()}
            >
              {selectableOptions.length === 0 ? (
                <div className="company-multi-select__empty">
                  Нет доступных организаций
                </div>
              ) : (
                selectableOptions.map((p) => (
                  <div
                    key={p.projectParticipantId}
                    className="company-multi-select__option"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() =>
                      handleToggle(
                        p,
                        !selectedIds.includes(p.projectParticipantId),
                      )
                    }
                  >
                    <span className="company-multi-select__option-label">
                      {getCompanyDisplayName(p)}
                    </span>
                    <Checkbox
                      checked={selectedIds.includes(p.projectParticipantId)}
                      disabled={isSelectDisabled}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggle(p, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                ))
              )}
            </div>
          );

          return (
            <div
              className={clsx(
                "company-multi-select__field",
                isSelectDisabled && "company-multi-select__field--disabled",
                companyListError && "company-multi-select__field--error",
              )}
            >
              <SelectedCompaniesChips
                selectedCompanies={selectedCompanies}
                onRemove={(item) => handleToggle(item, false)}
                onOpenSelect={() => setOpen(true)}
                isSelectDisabled={isSelectDisabled}
              />

              <Dropdown
                overlay={dropdownMenu}
                trigger={["click"]}
                open={open}
                onOpenChange={(v) => {
                  if (!isSelectDisabled) setOpen(v);
                }}
                disabled={isSelectDisabled}
              >
                <button
                  type="button"
                  className="company-multi-select__trigger"
                  disabled={isSelectDisabled}
                  onClick={(e) => e.stopPropagation()}
                >
                  Выбрать
                  <DownOutlined className="company-multi-select__arrow" />
                </button>
              </Dropdown>
            </div>
          );
        }}
      />
    </Form.Item>
  );
}
